import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import Calling from '../../api/Calling';
import { TextField, InputAdornment, IconButton, Select, MenuItem, CircularProgress, Skeleton } from '@mui/material';
import {
    Plus, Pencil, Trash2, Search, X, Check,
    ChevronUp, ChevronDown, Clock, Package, Tag, Users,
    RefreshCw, LayoutGrid, List, Gift, Text
} from 'lucide-react';
import generateCaptcha from '../../components/captcha';
import formatDate from '../../components/formatDate';
import TableSkeleton from '../../components/ul/TableSkeleton';

// Shared MUI light-field styling
const fieldSx = {
    '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: '#fff' },
};

const ReferralPlan = () => {
    const [loader, setLoader] = useState(true);
    const [planData, setPlanData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [viewMode, setViewMode] = useState('table');
    const [sortField, setSortField] = useState('r_invite_count');
    const [sortOrder, setSortOrder] = useState('asc');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [formData, setFormData] = useState({
        r_title: '', r_description: '', r_day: '', r_invite_count: '', r_lable: '', r_status: true
    });
    const [editId, setEditId] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchPlanData = async () => {
        setLoader(true);
        try {
            const response = await Calling.getReferralPlans();
            if (response?.status) {
                setPlanData(response?.plans || []);
            } else {
                toast.error(response?.message || 'Failed to fetch referral plan data');
            }
        } catch (error) {
            toast.error('Failed to fetch referral plan data');
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => { fetchPlanData(); }, []);

    useEffect(() => {
        let result = [...planData];
        if (searchQuery.trim() !== '') {
            result = result.filter(item =>
                item.r_title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.r_description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.r_lable?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.r_invite_count?.toString().includes(searchQuery)
            );
        }
        result.sort((a, b) => {
            let aValue = a[sortField], bValue = b[sortField];
            if (['r_title', 'r_description', 'r_lable'].includes(sortField)) {
                aValue = (aValue || '').toLowerCase();
                bValue = (bValue || '').toLowerCase();
            }
            if (['r_invite_count', 'r_day'].includes(sortField)) {
                aValue = Number(aValue) || 0;
                bValue = Number(bValue) || 0;
            }
            if (sortField === 'createdAt') {
                aValue = new Date(aValue || 0).getTime();
                bValue = new Date(bValue || 0).getTime();
            }
            if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });
        setFilteredData(result);
    }, [searchQuery, planData, sortField, sortOrder]);

    const handleSort = (field) => {
        if (sortField === field) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        else { setSortField(field); setSortOrder('asc'); }
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.r_title.trim()) errors.r_title = 'Title is required';
        if (!formData.r_invite_count.toString().trim()) errors.r_invite_count = 'Invite count required';
        else if (isNaN(formData.r_invite_count) || Number(formData.r_invite_count) < 1) errors.r_invite_count = 'Must be > 0';
        if (!formData.r_day.toString().trim()) errors.r_day = 'Duration required';
        else if (isNaN(formData.r_day) || Number(formData.r_day) < 1) errors.r_day = 'Must be > 0';
        if (!formData.r_lable.trim()) errors.r_lable = 'Label is required';
        if (!formData.r_description.trim()) errors.r_description = 'Description is required';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') setFormData(prev => ({ ...prev, [name]: checked }));
        else if (['r_day', 'r_invite_count'].includes(name)) {
            setFormData(prev => ({ ...prev, [name]: value.replace(/[^0-9]/g, '') }));
        } else setFormData(prev => ({ ...prev, [name]: value }));
        if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleStatusToggle = () => setFormData(prev => ({ ...prev, r_status: !prev.r_status }));

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setIsSubmitting(true);
        try {
            const body = {
                r_title: formData.r_title.trim(),
                r_description: formData.r_description.trim(),
                r_day: Number(formData.r_day),
                r_invite_count: Number(formData.r_invite_count),
                r_lable: formData.r_lable.trim(),
                r_status: formData.r_status
            };
            const response = await Calling.addReferralPlan(body);
            if (response?.status) {
                toast.success(response?.message || 'Referral Plan created!');
                setShowAddModal(false); resetForm(); fetchPlanData();
            } else toast.error(response?.message || 'Failed');
        } catch { toast.error('Failed to create plan'); }
        finally { setIsSubmitting(false); }
    };

    const handleEdit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setIsSubmitting(true);
        try {
            const body = {
                r_title: formData.r_title.trim(),
                r_description: formData.r_description.trim(),
                r_day: Number(formData.r_day),
                r_invite_count: Number(formData.r_invite_count),
                r_lable: formData.r_lable.trim(),
                r_status: formData.r_status
            };
            const response = await Calling.updateReferralPlan(editId, body);
            if (response?.status) {
                toast.success(response?.message || 'Referral Plan updated!');
                setShowEditModal(false); resetForm(); fetchPlanData();
            } else toast.error(response?.message || 'Failed');
        } catch { toast.error('Failed to update plan'); }
        finally { setIsSubmitting(false); }
    };

    const handleDeleteSingle = async (id) => {
        const check = generateCaptcha('Delete this referral plan? Solve:');
        if (!check) return toast.error('Incorrect answer', { autoClose: 2000 });
        setLoader(true);
        try {
            const response = await Calling.deleteReferralPlan(id);
            if (response?.status) { toast.success(response?.message || 'Deleted!'); fetchPlanData(); }
            else { toast.error(response?.message || 'Failed'); setLoader(false); }
        } catch { toast.error('Failed to delete'); setLoader(false); }
    };

    const openEditModal = (item) => {
        setFormData({
            r_title: item.r_title || '',
            r_description: item.r_description || '',
            r_day: item.r_day?.toString() || '',
            r_invite_count: item.r_invite_count?.toString() || '',
            r_lable: item.r_lable || '',
            r_status: item.r_status ?? true
        });
        setEditId(item._id);
        setShowEditModal(true);
    };

    const resetForm = () => {
        setFormData({ r_title: '', r_description: '', r_day: '', r_invite_count: '', r_lable: '', r_status: true });
        setFormErrors({}); setEditId(null);
    };

    const getLabelStyle = (label) => {
        const l = label?.toLowerCase() || '';
        if (l.includes('popular') || l.includes('best')) return 'bg-main-yellow/15 text-main-yellow border-main-yellow/30';
        if (l.includes('premium') || l.includes('pro')) return 'bg-main-exBlue/10 text-main-exBlue border-main-exBlue/30';
        if (l.includes('basic') || l.includes('beginner')) return 'bg-main-blue/10 text-main-blue border-main-blue/30';
        if (l.includes('gold')) return 'bg-main-yellow/15 text-main-yellow border-main-yellow/30';
        if (l.includes('silver')) return 'bg-main-subfont/10 text-main-subfont border-main-subfont/30';
        if (l.includes('bronze')) return 'bg-main-yellow/10 text-main-yellow border-main-yellow/20';
        return 'bg-main-subfont/10 text-main-subfont border-main-subfont/30';
    };

    const stats = {
        total: planData.length,
        active: planData.filter(p => p.r_status).length,
        inactive: planData.filter(p => !p.r_status).length,
        avgInvites: planData.length > 0 ? Math.round(planData.reduce((a, p) => a + (p.r_invite_count || 0), 0) / planData.length) : 0
    };

    // Loading State (MUI Skeleton mirroring the page)
    if (loader) {
        return (
            <div className="w-full text-main-font">
                {/* Header skeleton */}
                <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-6 gap-4">
                    <div className="flex items-center gap-4">
                        <Skeleton variant="rounded" width={48} height={48} sx={{ borderRadius: '12px' }} />
                        <div>
                            <Skeleton variant="text" width={220} height={30} />
                            <Skeleton variant="text" width={260} height={18} />
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: '8px' }} />
                        <Skeleton variant="rounded" width={130} height={42} sx={{ borderRadius: '8px' }} />
                    </div>
                </div>

                {/* Stats skeleton */}
                <div className="bg-main-white rounded-xl border border-main-border p-5 mb-6">
                    <Skeleton variant="text" width={90} height={18} sx={{ mb: 2 }} />
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {[...Array(4)].map((_, idx) => (
                            <Skeleton key={idx} variant="rounded" height={76} sx={{ borderRadius: '8px' }} />
                        ))}
                    </div>
                </div>

                {/* Search bar skeleton */}
                <div className="bg-main-white rounded-xl border border-main-border p-5 mb-6">
                    <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                        <Skeleton variant="rounded" height={44} sx={{ borderRadius: '12px', flex: 1, width: '100%', maxWidth: 420 }} />
                        <div className="flex items-center gap-3">
                            <Skeleton variant="rounded" width={92} height={42} sx={{ borderRadius: '8px' }} />
                            <Skeleton variant="rounded" width={170} height={42} sx={{ borderRadius: '8px' }} />
                        </div>
                    </div>
                </div>

                {/* Table skeleton */}
                <div className="bg-main-white rounded-xl border border-main-border overflow-hidden">
                    <TableSkeleton rows={6} cols={7} />
                </div>
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="w-full text-main-font"
        >
            {/* Page Header */}
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-6 gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-main-blue rounded-xl flex items-center justify-center shadow-lg shadow-main-blue/30">
                        <Gift size={24} className="text-main-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-main-font">Referral Plans</h1>
                        <p className="text-sm text-main-subfont mt-0.5">Manage referral invite thresholds and rewards</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button onClick={fetchPlanData} className="w-10 h-10 border border-main-border text-main-subfont rounded-lg hover:bg-main-th hover:text-main-blue hover:border-main-blue/30 transition-all flex items-center justify-center" title="Refresh">
                        <RefreshCw size={18} />
                    </button>
                    <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 bg-main-blue text-main-white px-5 py-2.5 rounded-lg font-semibold transition-all hover:shadow-lg hover:shadow-main-blue/30 active:scale-[0.98]">
                        <Plus size={18} strokeWidth={2.5} />
                        <span>Add Plan</span>
                    </button>
                </div>
            </div>

            {/* Stats Section */}
            <div className="bg-main-white rounded-xl border border-main-border p-5 mb-6">
                <h2 className="text-sm font-semibold text-main-subfont uppercase tracking-wider mb-4">Overview</h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard title="Total Plans" value={stats.total} icon={Package} colorClass="bg-main-exBlue" />
                    <StatCard title="Active Plans" value={stats.active} icon={Check} colorClass="bg-main-blue" />
                    <StatCard title="Inactive" value={stats.inactive} icon={X} colorClass="bg-main-red" />
                    <StatCard title="Avg. Invites" value={stats.avgInvites} icon={Users} colorClass="bg-main-yellow" />
                </div>
            </div>

            {/* Search & Controls */}
            <div className="bg-main-white rounded-xl border border-main-border p-5 mb-6">
                <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                    <div className="relative flex-1 w-full md:max-w-md">
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search by title, label, description..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={fieldSx}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search size={18} className="text-main-subfont" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchQuery ? (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setSearchQuery('')} aria-label="clear search">
                                                <X size={16} className="text-main-subfont" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                },
                            }}
                        />
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center bg-main-bg border border-main-border rounded-lg p-1">
                            <button onClick={() => setViewMode('table')} className={`p-2 rounded-md transition-all ${viewMode === 'table' ? 'bg-main-white shadow-sm text-main-blue' : 'text-main-subfont hover:text-main-font'}`}>
                                <List size={18} />
                            </button>
                            <button onClick={() => setViewMode('card')} className={`p-2 rounded-md transition-all ${viewMode === 'card' ? 'bg-main-white shadow-sm text-main-blue' : 'text-main-subfont hover:text-main-font'}`}>
                                <LayoutGrid size={18} />
                            </button>
                        </div>
                        <Select
                            value={`${sortField}-${sortOrder}`}
                            onChange={(e) => { const [f, o] = e.target.value.split('-'); setSortField(f); setSortOrder(o); }}
                            size="small"
                            sx={{
                                borderRadius: '12px',
                                backgroundColor: '#fff',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                minWidth: 168,
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--main-border, #e5e7eb)' },
                            }}
                        >
                            <MenuItem value="r_invite_count-asc">Invites: Low to High</MenuItem>
                            <MenuItem value="r_invite_count-desc">Invites: High to Low</MenuItem>
                            <MenuItem value="r_title-asc">Title: A-Z</MenuItem>
                            <MenuItem value="r_title-desc">Title: Z-A</MenuItem>
                            <MenuItem value="r_day-asc">Duration: Shortest</MenuItem>
                            <MenuItem value="r_day-desc">Duration: Longest</MenuItem>
                            <MenuItem value="createdAt-desc">Newest First</MenuItem>
                            <MenuItem value="createdAt-asc">Oldest First</MenuItem>
                        </Select>
                    </div>
                </div>
            </div>

            {/* Content Area */}
            <div className="bg-main-white rounded-xl border border-main-border overflow-hidden">
                {filteredData.length === 0 ? (
                    <EmptyState searchQuery={searchQuery} onAddClick={() => setShowAddModal(true)} onClear={() => setSearchQuery('')} />
                ) : viewMode === 'card' ? (
                    <div className="p-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                            {filteredData.map((item) => (
                                <PlanCard key={item._id} item={item} getLabelStyle={getLabelStyle} onEdit={openEditModal} onDelete={handleDeleteSingle} />
                            ))}
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="hidden lg:grid lg:grid-cols-12 gap-4 px-5 py-3.5 bg-main-th text-xs font-semibold text-main-subfont uppercase tracking-wider border-b border-main-border">
                            <div className="col-span-1">#</div>
                            <div className="col-span-3"><SortableHeader field="r_title" label="Plan Details" handleSort={handleSort} sortField={sortField} sortOrder={sortOrder} /></div>
                            <div className="col-span-2"><SortableHeader field="r_invite_count" label="Invites" handleSort={handleSort} sortField={sortField} sortOrder={sortOrder} /></div>
                            <div className="col-span-2"><SortableHeader field="r_day" label="Duration" handleSort={handleSort} sortField={sortField} sortOrder={sortOrder} /></div>
                            <div className="col-span-2">Label</div>
                            <div className="col-span-1">Status</div>
                            <div className="col-span-1 text-right">Actions</div>
                        </div>
                        <div className="divide-y divide-main-border/50">
                            {filteredData.map((item, idx) => (
                                <TableRow key={item._id} item={item} index={idx} getLabelStyle={getLabelStyle} onEdit={openEditModal} onDelete={handleDeleteSingle} />
                            ))}
                        </div>
                    </>
                )}
                {filteredData.length > 0 && (
                    <div className="px-5 py-4 border-t border-main-border bg-main-bg/50">
                        <p className="text-sm text-main-subfont text-center">
                            Showing <span className="font-semibold text-main-font">{filteredData.length}</span> of <span className="font-semibold text-main-font">{planData.length}</span> referral plans
                        </p>
                    </div>
                )}
            </div>

            {/* Modals */}
            <Modal show={showAddModal} onClose={() => { setShowAddModal(false); resetForm(); }} title="Create Referral Plan" icon={Plus}>
                <PlanForm formData={formData} formErrors={formErrors} isSubmitting={isSubmitting} onChange={handleInputChange} onStatusToggle={handleStatusToggle} onSubmit={handleAdd} onCancel={() => { setShowAddModal(false); resetForm(); }} submitLabel="Create Plan" />
            </Modal>
            <Modal show={showEditModal} onClose={() => { setShowEditModal(false); resetForm(); }} title="Edit Referral Plan" icon={Pencil}>
                <PlanForm formData={formData} formErrors={formErrors} isSubmitting={isSubmitting} onChange={handleInputChange} onStatusToggle={handleStatusToggle} onSubmit={handleEdit} onCancel={() => { setShowEditModal(false); resetForm(); }} submitLabel="Update Plan" />
            </Modal>
        </motion.div>
    );
};

export default ReferralPlan;

// ==================== SUB-COMPONENTS ====================

const StatCard = ({ title, value, icon: Icon, colorClass }) => (
    <div className="flex items-center gap-4 p-4 bg-main-bg rounded-lg border border-main-border/50 hover:border-main-blue/30 hover:shadow-md transition-all">
        <div className={`w-11 h-11 ${colorClass} rounded-lg flex items-center justify-center shadow-md`}>
            <Icon size={20} className="text-main-white" />
        </div>
        <div>
            <p className="text-xs text-main-subfont font-medium uppercase tracking-wide">{title}</p>
            <p className="text-2xl font-bold text-main-font mt-0.5">{value}</p>
        </div>
    </div>
);

const EmptyState = ({ searchQuery, onAddClick, onClear }) => (
    <div className="p-12 text-center">
        <div className="w-16 h-16 bg-main-bg border border-main-border rounded-xl flex items-center justify-center mx-auto mb-4">
            <Gift size={28} className="text-main-subfont" />
        </div>
        <h3 className="text-lg font-semibold text-main-font">{searchQuery ? 'No plans found' : 'No referral plans yet'}</h3>
        <p className="text-main-subfont mt-1 mb-5 max-w-sm mx-auto text-sm">{searchQuery ? 'Try different search keywords or clear the filter' : 'Create your first referral plan to start rewarding users'}</p>
        {searchQuery ? (
            <button onClick={onClear} className="text-main-blue font-semibold hover:underline text-sm">Clear search</button>
        ) : (
            <button onClick={onAddClick} className="inline-flex items-center gap-2 bg-main-blue text-main-white px-5 py-2.5 rounded-lg font-semibold hover:shadow-lg hover:shadow-main-blue/30 transition-all text-sm">
                <Plus size={16} strokeWidth={2.5} />Create First Plan
            </button>
        )}
    </div>
);

const PlanCard = ({ item, getLabelStyle, onEdit, onDelete }) => {
    const labelStyle = getLabelStyle(item.r_lable);

    return (
        <div className="bg-main-white rounded-xl border border-main-border overflow-hidden hover:border-main-blue/40 hover:shadow-lg transition-all group">
            <div className="p-5">
                <div className="flex items-start gap-3">
                    <div className="w-12 h-12 bg-main-blue rounded-lg flex items-center justify-center text-main-white font-bold text-lg shadow-md shadow-main-blue/25 flex-shrink-0">
                        {item.r_title?.charAt(0)?.toUpperCase() || 'R'}
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-main-font truncate">{item.r_title}</h3>
                        <div className="flex items-center gap-2 mt-1.5">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold border ${labelStyle}`}>
                                <Tag size={10} />{item.r_lable}
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold flex items-center gap-1 ${item.r_status ? 'bg-main-blue/10 text-main-blue' : 'bg-main-red/10 text-main-red'}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${item.r_status ? 'bg-main-blue' : 'bg-main-red'}`}></span>
                                {item.r_status ? 'Active' : 'Inactive'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats Section */}
            <div className="px-5 py-4 border-t border-main-border bg-main-bg">
                <div className="grid grid-cols-2 gap-3">
                    <div className="bg-main-white p-3 rounded-lg border border-main-border">
                        <div className="flex items-center gap-2 text-main-exBlue mb-1">
                            <Users size={14} />
                            <span className="text-xs font-semibold uppercase">Invites</span>
                        </div>
                        <p className="text-xl font-bold text-main-font">{item.r_invite_count}</p>
                    </div>
                    <div className="bg-main-white p-3 rounded-lg border border-main-border">
                        <div className="flex items-center gap-2 text-main-exBlue mb-1">
                            <Clock size={14} />
                            <span className="text-xs font-semibold uppercase">Duration</span>
                        </div>
                        <p className="text-xl font-bold text-main-font">{item.r_day} <span className="text-sm font-medium text-main-subfont">Days</span></p>
                    </div>
                </div>
            </div>

            {item.r_description && (
                <div className="px-5 py-2.5 border-t border-main-border">
                    <p className="text-xs text-main-subfont line-clamp-2">{item.r_description}</p>
                </div>
            )}

            <div className="px-5 py-3 flex items-center justify-between border-t border-main-border">
                <span className="text-xs text-main-subfont">{formatDate(item.createdAt)}</span>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-all">
                    <button onClick={() => onEdit(item)} className="p-2 text-main-exBlue hover:bg-main-exBlue/10 rounded-lg transition-colors" title="Edit"><Pencil size={16} /></button>
                    <button onClick={() => onDelete(item._id)} className="p-2 text-main-delete hover:bg-main-delete/10 rounded-lg transition-colors" title="Delete"><Trash2 size={16} /></button>
                </div>
            </div>
        </div>
    );
};

const TableRow = ({ item, index, getLabelStyle, onEdit, onDelete }) => {
    const labelStyle = getLabelStyle(item.r_lable);

    return (
        <>
            <div className="hidden lg:grid lg:grid-cols-12 gap-4 px-5 py-4 items-center hover:bg-main-thHover transition-colors group">
                <div className="col-span-1 text-sm font-medium text-main-subfont">{index + 1}</div>
                <div className="col-span-3 flex items-center gap-3">
                    <div className="w-10 h-10 bg-main-blue rounded-lg flex items-center justify-center text-main-white font-semibold shadow-sm flex-shrink-0">{item.r_title?.charAt(0)?.toUpperCase() || 'R'}</div>
                    <div className="min-w-0">
                        <p className="font-semibold text-main-font truncate">{item.r_title}</p>
                        {item.r_description && <p className="text-xs text-main-subfont truncate mt-0.5">{item.r_description}</p>}
                    </div>
                </div>
                <div className="col-span-2">
                    <span className="inline-flex items-center gap-1.5 font-bold text-main-exBlue bg-main-exBlue/10 px-3 py-1.5 rounded-lg text-sm">
                        <Users size={14} />{item.r_invite_count}
                    </span>
                </div>
                <div className="col-span-2 flex items-center gap-1 text-main-subfont text-sm">
                    <Clock size={13} /><span className="font-medium">{item.r_day} Days</span>
                </div>
                <div className="col-span-2">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border ${labelStyle}`}><Tag size={11} />{item.r_lable}</span>
                </div>
                <div className="col-span-1">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold ${item.r_status ? 'bg-main-blue/10 text-main-blue' : 'bg-main-red/10 text-main-red'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${item.r_status ? 'bg-main-blue' : 'bg-main-red'}`}></span>
                        {item.r_status ? 'Active' : 'Inactive'}
                    </span>
                </div>
                <div className="col-span-1 flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-all">
                    <button onClick={() => onEdit(item)} className="p-1.5 text-main-exBlue hover:bg-main-exBlue/10 rounded-lg transition-colors"><Pencil size={15} /></button>
                    <button onClick={() => onDelete(item._id)} className="p-1.5 text-main-delete hover:bg-main-delete/10 rounded-lg transition-colors"><Trash2 size={15} /></button>
                </div>
            </div>

            {/* Mobile Row */}
            <div className="lg:hidden p-4 hover:bg-main-thHover transition-colors">
                <div className="flex gap-3">
                    <div className="w-12 h-12 bg-main-blue rounded-lg flex items-center justify-center text-main-white font-bold text-lg flex-shrink-0 shadow-sm">{item.r_title?.charAt(0)?.toUpperCase() || 'R'}</div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                            <div>
                                <h4 className="font-bold text-main-font">{item.r_title}</h4>
                                <p className="text-sm text-main-exBlue font-semibold mt-1">{item.r_invite_count} Invites • {item.r_day} Days</p>
                            </div>
                            <div className="flex gap-1">
                                <button onClick={() => onEdit(item)} className="p-1.5 text-main-exBlue"><Pencil size={17} /></button>
                                <button onClick={() => onDelete(item._id)} className="p-1.5 text-main-delete"><Trash2 size={17} /></button>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded border ${labelStyle}`}><Tag size={10} />{item.r_lable}</span>
                            <span className={`text-xs px-2 py-0.5 rounded font-semibold flex items-center gap-1 ${item.r_status ? 'bg-main-blue/10 text-main-blue' : 'bg-main-red/10 text-main-red'}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${item.r_status ? 'bg-main-blue' : 'bg-main-red'}`}></span>
                                {item.r_status ? 'Active' : 'Inactive'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

const Modal = ({ show, onClose, title, icon: Icon, children }) => (
    <AnimatePresence>
        {show && (
            <motion.div
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
            >
                <div className="fixed inset-0 bg-main-font/50 backdrop-blur-sm" onClick={onClose}></div>
                <motion.div
                    className="relative bg-main-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden border border-main-border"
                    initial={{ scale: 0.95, y: 12, opacity: 0 }}
                    animate={{ scale: 1, y: 0, opacity: 1 }}
                    exit={{ scale: 0.95, y: 12, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                >
                    <div className="flex items-center justify-between p-5 border-b border-main-border bg-main-th">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-main-blue rounded-lg flex items-center justify-center shadow-md shadow-main-blue/25"><Icon size={16} className="text-main-white" /></div>
                            <h3 className="text-lg font-bold text-main-font">{title}</h3>
                        </div>
                        <button onClick={onClose} className="w-8 h-8 hover:bg-main-white rounded-lg flex items-center justify-center transition-colors"><X size={18} className="text-main-subfont" /></button>
                    </div>
                    <div className="p-5 overflow-y-auto max-h-[calc(90vh-80px)]">{children}</div>
                </motion.div>
            </motion.div>
        )}
    </AnimatePresence>
);

const PlanForm = ({ formData, formErrors, isSubmitting, onChange, onStatusToggle, onSubmit, onCancel, submitLabel }) => {
    return (
        <form onSubmit={onSubmit} className="space-y-4">
            <FormInput label="Plan Title" name="r_title" value={formData.r_title} onChange={onChange} placeholder="e.g. Gold Tier, Premium Plan" error={formErrors.r_title} icon={Text} />
            <div className="grid grid-cols-2 gap-3">
                <FormInput label="Invite Count" name="r_invite_count" value={formData.r_invite_count} onChange={onChange} placeholder="e.g. 10" error={formErrors.r_invite_count} icon={Users} />
                <FormInput label="Duration (Days)" name="r_day" value={formData.r_day} onChange={onChange} placeholder="e.g. 30" error={formErrors.r_day} icon={Clock} />
            </div>
            <FormInput label="Label" name="r_lable" value={formData.r_lable} onChange={onChange} placeholder="e.g. Popular, Premium, Basic" error={formErrors.r_lable} icon={Tag} />
            <div>
                <label className="block text-sm font-semibold text-main-font mb-1.5">Description <span className="text-main-red">*</span></label>
                <TextField
                    name="r_description"
                    value={formData.r_description}
                    onChange={onChange}
                    placeholder="Describe the benefits and rewards of this plan..."
                    multiline
                    minRows={3}
                    fullWidth
                    size="small"
                    error={!!formErrors.r_description}
                    helperText={formErrors.r_description || ''}
                    sx={fieldSx}
                />
            </div>
            <div className="flex items-center justify-between p-3.5 bg-main-bg border border-main-border rounded-lg">
                <div>
                    <p className="font-semibold text-main-font text-sm">Plan Status</p>
                    <p className="text-xs text-main-subfont mt-0.5">Enable or disable this referral plan</p>
                </div>
                <div onClick={onStatusToggle} className={`relative w-12 h-6 rounded-full cursor-pointer transition-all ${formData.r_status ? 'bg-main-blue' : 'bg-main-border'}`}>
                    <span className={`absolute top-0.5 w-5 h-5 bg-main-white rounded-full shadow-md transition-all ${formData.r_status ? 'left-6' : 'left-0.5'}`} />
                </div>
            </div>
            <div className="flex gap-3 pt-2">
                <button type="button" onClick={onCancel} className="flex-1 px-4 py-2.5 border border-main-border text-main-font rounded-lg hover:bg-main-bg font-semibold text-sm transition-all">Cancel</button>
                <button type="submit" disabled={isSubmitting} className="flex-1 px-4 py-2.5 bg-main-blue text-main-white rounded-lg font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-main-blue/30 transition-all active:scale-[0.98]">
                    {isSubmitting ? (<><CircularProgress size={16} color="inherit" />Processing...</>) : (<><Check size={16} strokeWidth={2.5} />{submitLabel}</>)}
                </button>
            </div>
        </form>
    );
};

const FormInput = ({ label, name, value, onChange, placeholder, error, icon: Icon }) => (
    <div>
        <label className="block text-sm font-semibold text-main-font mb-1.5">{label} <span className="text-main-red">*</span></label>
        <TextField
            fullWidth
            size="small"
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            error={!!error}
            helperText={error || ''}
            sx={fieldSx}
            slotProps={{
                input: {
                    startAdornment: Icon ? (
                        <InputAdornment position="start">
                            <Icon size={17} className="text-main-subfont" />
                        </InputAdornment>
                    ) : undefined,
                },
            }}
        />
    </div>
);

const SortIcon = ({ field, sortField, sortOrder }) => {
    if (sortField !== field) return <div className="flex flex-col ml-1 opacity-30"><ChevronUp size={10} className="-mb-0.5" /><ChevronDown size={10} /></div>;
    return sortOrder === 'asc' ? <ChevronUp size={14} className="ml-1 text-main-blue" /> : <ChevronDown size={14} className="ml-1 text-main-blue" />;
};

const SortableHeader = ({ field, label, handleSort, sortField, sortOrder }) => (
    <div className="flex items-center cursor-pointer hover:text-main-blue transition-colors select-none" onClick={() => handleSort(field)}>
        <span>{label}</span><SortIcon field={field} sortField={sortField} sortOrder={sortOrder} />
    </div>
);
